import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { isInteger } from 'utilities/validators';

import * as store from 'store';

import { getFilteredParametersValuesNotEmptyLength } from 'store/dataset';

import {
  getSamplesAmount, doSamplesExist
} from 'store/scenes/bootstrapCIs/samples/selectors';
import { BOOTSTRAP_CIS_FORM } from 'store/form';

import SettingsFormComponent from './Component';

const MAX_NUMBER_OF_SAMPLES = 4500;

class SettingsFormContainer extends React.PureComponent {
  constructor (props) {
    super(props);

    this.handleResetClick = () => this.props.resetBootstrapCIs();
    this.handleGenerateSamplesClick = () => this.props.bootstrapSamples();

    this.handlePrimaryParameterChange = parameterName => {
      // Resets test selection dropdown option
      this.props.setTestSelectionType(parameterName);
    };

    this.handleSecondaryParameterChange = secondaryParameter => {
      this.props.change('testSelection', secondaryParameter ? 'slope' : 'mean');
    };

    this.numberOfSamplesValidator = value => {
      const minValue = 2;
      const maxValue = this.maxNumberOfSamplesValue;

      const error = `
        Should be an integer greater than or equal ${minValue} and less than or
        equal ${maxValue}! Otherwise an out-of-memory exception can be raised!
      `;

      return isInteger(value) === null && value >= minValue && value <= maxValue ? null : error;
    };
  }

  componentDidMount () {
    if (this.props.samplesExist) {
      return;
    }

    this.props.initialize({
      primaryParameter: '',
      secondaryParameter: '',
      primaryParameterLevel: '',

      alpha: 0.05,

      numberOfSamples: 100,

      tailType: 'left',

      cutoffLeft: '',
      cutoffRight: ''
    });
  }

  get optionsControlsVisible () {
    const {
      primaryParameterType,
      primaryParameterSelected,
      primaryParameterLevelSelected
    } = this.props;

    if (!primaryParameterSelected) {
      return false;
    }

    const isNumerical = primaryParameterType === 'numerical';
    const isCategorical = primaryParameterType === 'categorical';

    return isNumerical || (isCategorical && primaryParameterLevelSelected);
  }

  get maxNumberOfSamplesValue () {
    return MAX_NUMBER_OF_SAMPLES - getSamplesAmount(store.default.getState());
  }

  get showEmptyDatasetWarning () {
    return this.props.datasetLength <= 0;
  }

  get generateSamplesButtonDisabled () {
    return this.props.invalid || this.showEmptyDatasetWarning;
  }

  render () {
    return (
      <SettingsFormComponent
        form={this.props.form}
        onResetClick={this.handleResetClick}
        samplesExist={this.props.samplesExist}
        optionsControlsVisible={this.optionsControlsVisible}
        primaryParameterName={this.props.primaryParameterName}
        showEmptyDatasetWarning={this.showEmptyDatasetWarning}
        maxNumberOfSamplesValue={this.maxNumberOfSamplesValue}
        onGenerateSamplesClick={this.handleGenerateSamplesClick}
        numberOfSamplesValidator={this.numberOfSamplesValidator}
        primaryParameterLevels={this.props.primaryParameterLevels}
        onPrimaryParameterChange={this.handlePrimaryParameterChange}
        onSecondaryParameterChange={this.handleSecondaryParameterChange}
        generateSamplesButtonDisabled={this.generateSamplesButtonDisabled}
        secondaryParameterDropdownVisible={this.props.numericalPrimaryParameter}
      />
    );
  }
}

SettingsFormContainer.propTypes = {
  form: PropTypes.string.isRequired,

  primaryParameterName: PropTypes.string,
  primaryParameterType: PropTypes.string,
  primaryParameterLevels: PropTypes.array,

  samplesExist: PropTypes.bool.isRequired,
  numericalPrimaryParameter: PropTypes.bool.isRequired,

  primaryParameterSelected: PropTypes.bool.isRequired,
  primaryParameterLevelSelected: PropTypes.bool.isRequired,

  change: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  initialize: PropTypes.func.isRequired,

  bootstrapSamples: PropTypes.func.isRequired,
  resetBootstrapCIs: PropTypes.func.isRequired,
  setTestSelectionType: PropTypes.func.isRequired,

  datasetLength: PropTypes.number
};

SettingsFormContainer.defaultProps = {
  primaryParameterName: null,
  primaryParameterType: null,
  primaryParameterLevels: null,
  datasetLength: null
};

const mapStateToProps = state => {
  const primaryParameterName = store.form.bootstrapCIs.getPrimaryParameterName(state);
  const secondaryParameterName = store.form.bootstrapCIs.getSecondaryParameterName(state);

  return ({
    datasetLength: getFilteredParametersValuesNotEmptyLength(state, primaryParameterName, secondaryParameterName),

    primaryParameterSelected: store.form.bootstrapCIs.isPrimaryParameterSelected(state),
    numericalPrimaryParameter: store.form.bootstrapCIs.isPrimaryParameterNumerical(state),
    primaryParameterLevelSelected: store.form.bootstrapCIs.isPrimaryParameterLevelSelected(state),

    primaryParameterName,
    primaryParameterType: store.form.bootstrapCIs.getPrimaryParameterType(state),
    primaryParameterLevels: store.form.bootstrapCIs.getPrimaryParameterLevels(state),

    samplesExist: doSamplesExist(state)
  });
};

const mapDispatchToProps = {
  bootstrapSamples: store.scenes.bootstrapCIs.bootstrapSamples,
  resetBootstrapCIs: store.scenes.bootstrapCIs.resetBootstrapCIs,
  setTestSelectionType: store.scenes.bootstrapCIs.setTestSelectionType
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({ form: BOOTSTRAP_CIS_FORM, destroyOnUnmount: false })(
    SettingsFormContainer
  )
);
